// Client-side Chinese Checkers engine — a faithful port of the backend's
// ChineseCheckersGame + Board so the game runs entirely in the browser (no API).
// The AI move selection is already client-side; this replaces the last server
// dependency (game state, legal moves, win detection).

import type { Move, Piece, Position } from '../stores/gameStore'
import { generateMoves } from '../ai/moveGen'
import { getPlayer1Home, getPlayer2Home } from '../utils/board'

const MAX_MOVES = 200 // draw after this many plies (matches backend)
const keyOf = (p: Position) => `${p.q},${p.r}`

const P1_HOME = getPlayer1Home() // bottom triangle
const P2_HOME = getPlayer2Home() // top triangle
// Each player's destination is the opposite home.
const GOAL: Record<number, Set<string>> = { 1: P2_HOME, 2: P1_HOME }
const HOME: Record<number, Set<string>> = { 1: P1_HOME, 2: P2_HOME }

// Matches parseGameResponse's shape so the hooks can drop it straight into the store.
export interface EngineState {
  gameId: string
  pieces: Piece[]
  currentPlayer: number
  winner: number | null
  legalMoves: Move[]
  moveHistory: Move[]
}

let gameCounter = 0

function piecesFromHome(home: Set<string>, player: number): Piece[] {
  return [...home].map((k) => {
    const [q, r] = k.split(',').map(Number)
    return { position: { q, r }, player }
  })
}

function legalMovesFor(pieces: Piece[], player: number): Move[] {
  return generateMoves(pieces, player, { goalZone: GOAL[player], homeZone: HOME[player] })
}

// Port of Board.check_winner: a player wins when all their marbles sit in the
// opposite home, or that home is completely full with at least one of theirs.
function checkWinner(pieces: Piece[]): number | null {
  const occupied = new Map<string, number>()
  for (const p of pieces) occupied.set(keyOf(p.position), p.player)

  const p1 = pieces.filter((p) => p.player === 1).map((p) => keyOf(p.position))
  if (p1.length > 0 && p1.every((k) => P2_HOME.has(k))) return 1

  const p2 = pieces.filter((p) => p.player === 2).map((p) => keyOf(p.position))
  if (p2.length > 0 && p2.every((k) => P1_HOME.has(k))) return 2

  const p2HomeFull = [...P2_HOME].every((k) => occupied.has(k))
  if (p2HomeFull && [...P2_HOME].some((k) => occupied.get(k) === 1)) return 1

  const p1HomeFull = [...P1_HOME].every((k) => occupied.has(k))
  if (p1HomeFull && [...P1_HOME].some((k) => occupied.get(k) === 2)) return 2

  return null
}

// Fresh 2-player game: red (1) at the bottom, blue (2) at the top, red to move.
export function createNewGame(): EngineState {
  gameCounter += 1
  const pieces = [...piecesFromHome(P1_HOME, 1), ...piecesFromHome(P2_HOME, 2)]
  const currentPlayer = 1
  return {
    gameId: `game_${gameCounter}`,
    pieces,
    currentPlayer,
    winner: null,
    legalMoves: legalMovesFor(pieces, currentPlayer),
    moveHistory: [],
  }
}

// Apply a move for the current player and return the next state. Mirrors
// Board.move_piece + ChineseCheckersGame.make_move (win/draw check, swap player).
export function applyMove(state: EngineState, move: Move): EngineState {
  const pieces = state.pieces.map((p) =>
    p.position.q === move.start.q && p.position.r === move.start.r
      ? { position: { q: move.end.q, r: move.end.r }, player: p.player }
      : p,
  )
  const moveHistory = [...state.moveHistory, move]

  let winner = checkWinner(pieces)
  if (winner === null && moveHistory.length >= MAX_MOVES) winner = 0 // draw

  const nextPlayer = 3 - state.currentPlayer
  return {
    gameId: state.gameId,
    pieces,
    currentPlayer: nextPlayer,
    winner,
    legalMoves: winner === null ? legalMovesFor(pieces, nextPlayer) : [],
    moveHistory,
  }
}
